import { round2 } from "../contexts/AppContext";

const positiveNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
};

const pickFirstPositive = (candidates) => {
  for (const candidate of candidates) {
    const value = positiveNumber(candidate?.value);
    if (value != null) {
      return {
        value,
        field: candidate.field,
        priceInfo: candidate.priceInfo || null,
        alreadyConverted: candidate.alreadyConverted === true,
      };
    }
  }
  return null;
};

const getFlightDepartureTimes = (row) => {
  const fullDeal = row?.fullDeal || row || {};
  const linkedFlightsData =
    row?.linkedFlights ||
    fullDeal?.linkedFlights ||
    fullDeal?.flight ||
    row?.flight ||
    null;

  const flights = (() => {
    if (!linkedFlightsData) return [];
    if (Array.isArray(linkedFlightsData)) return linkedFlightsData;
    if (Array.isArray(linkedFlightsData.flight)) return linkedFlightsData.flight;
    if (linkedFlightsData.flight) return [linkedFlightsData.flight];
    return [linkedFlightsData];
  })();

  const extractTime = (flightOrLeg) => {
    const leg =
      flightOrLeg?.legs?.[0] ||
      (Array.isArray(flightOrLeg?.leg) ? flightOrLeg.leg[0] : flightOrLeg?.leg) ||
      flightOrLeg;
    const depDate = leg?.depDate || flightOrLeg?.depDate || "";
    if (typeof depDate === "string" && depDate.includes(" ")) {
      return depDate.split(" ")[1] || null;
    }
    return leg?.depTime || flightOrLeg?.depTime || null;
  };

  return {
    outboundTime: extractTime(flights[0]),
    inboundTime: extractTime(flights[1]),
  };
};

export const formatMonthlyIlsPrice = (price) => {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
    return "0₪";
  }
  return `${round2(numericPrice)}₪`;
};

export const resolveMonthlyDisplayPrice = (row, options = {}) => {
  const fullDeal = row?.fullDeal || row || {};
  const ourPrice =
    row?.ourPrice ||
    row?.ourprice ||
    fullDeal?.ourPrice ||
    fullDeal?.ourprice ||
    null;
  const ourTotalFare =
    ourPrice?.ourTotalFare ||
    ourPrice?.our_total_fare ||
    null;
  const fallbackPriceInfo = options?.fallbackPriceInfo || null;
  const fallbackOurTotalFare =
    fallbackPriceInfo?.ourTotalFare ||
    fallbackPriceInfo?.our_total_fare ||
    null;
  const fareDetails = fullDeal?.fareDetails || row?.fareDetails || {};
  const fullDealPricingSnapshot =
    (fullDeal?.pricingSnapshot && typeof fullDeal.pricingSnapshot === "object"
      ? fullDeal.pricingSnapshot
      : null) ||
    (row?.pricingSnapshot && typeof row.pricingSnapshot === "object"
      ? row.pricingSnapshot
      : null);
  const pricingSnapshot = fullDealPricingSnapshot || ourPrice?.pricingSnapshot || {};
  const sourceCurrency =
    row?.sourceCurrency ||
    row?.source_currency ||
    row?.currency ||
    row?.displayCurrency ||
    ourTotalFare?.originalCurrency ||
    ourTotalFare?.fx_currency ||
    ourPrice?.currency ||
    fareDetails?.currency ||
    null;
  const normalizedCurrency =
    sourceCurrency != null ? String(sourceCurrency).trim().toUpperCase() : null;
  const exchangeRate =
    positiveNumber(row?.exchangeRate) ||
    positiveNumber(row?.exchange_rate) ||
    positiveNumber(row?.exchangeRateUsed) ||
    positiveNumber(row?.exchange_rate_used) ||
    positiveNumber(ourTotalFare?.fx_rate) ||
    positiveNumber(ourTotalFare?.currencyRate) ||
    positiveNumber(ourPrice?.currencyRate) ||
    positiveNumber(pricingSnapshot?.exchangeRateUsed) ||
    null;
  const explicitConverted =
    row?.priceAlreadyConverted === true ||
    row?.price_already_converted === true ||
    ourPrice?.priceAlreadyConverted === true ||
    ourPrice?.price_already_converted === true ||
    ourTotalFare?.priceAlreadyConverted === true ||
    ourTotalFare?.price_already_converted === true;

  const selected = pickFirstPositive([
    {
      field: "fullDeal.pricingSnapshot.finalClientPriceIls",
      value: fullDealPricingSnapshot?.finalClientPriceIls,
      priceInfo: ourPrice,
      alreadyConverted: true,
    },
    {
      field: "fullDeal.pricingSnapshot.finalPriceIls",
      value: fullDealPricingSnapshot?.finalPriceIls,
      priceInfo: ourPrice,
      alreadyConverted: true,
    },
    {
      field: "fullDeal.pricingSnapshot.final_price",
      value: fullDealPricingSnapshot?.final_price,
      priceInfo: ourPrice,
      alreadyConverted: true,
    },
    {
      field: "ourPrice.ourTotalFare.priceWithCommissionAndCreditCardFee",
      value: ourTotalFare?.priceWithCommissionAndCreditCardFee,
      priceInfo: ourPrice,
      alreadyConverted: true,
    },
    {
      field: "ourPrice.ourTotalFare.priceWithCommission",
      value: ourTotalFare?.priceWithCommission,
      priceInfo: ourPrice,
      alreadyConverted: true,
    },
    {
      field: "fallbackPriceInfo.ourTotalFare.priceWithCommissionAndCreditCardFee",
      value: fallbackOurTotalFare?.priceWithCommissionAndCreditCardFee,
      priceInfo: fallbackPriceInfo,
      alreadyConverted: true,
    },
  ]);

  let totalPrice = selected?.value ?? null;
  let selectedPriceField = selected?.field ?? null;
  let priceInfo = selected?.priceInfo || ourPrice || fallbackPriceInfo || null;
  let priceAlreadyConverted =
    explicitConverted ||
    selected?.alreadyConverted === true ||
    normalizedCurrency === "ILS" ||
    totalPrice != null;

  if (totalPrice == null && options?.allowSourceCurrencyFallback === true) {
    const sourcePrice =
      positiveNumber(row?.sourcePrice) ||
      positiveNumber(row?.source_price) ||
      positiveNumber(fullDeal?.dealFare?.totalFare) ||
      positiveNumber(row?.price) ||
      positiveNumber(row?.packagePrice);
    if (sourcePrice != null && normalizedCurrency && normalizedCurrency !== "ILS" && exchangeRate) {
      totalPrice = round2(sourcePrice * exchangeRate);
      selectedPriceField = "sourcePrice*exchangeRate";
      priceAlreadyConverted = false;
    } else if (sourcePrice != null && normalizedCurrency === "ILS") {
      totalPrice = sourcePrice;
      selectedPriceField = "sourcePrice.currencyILS";
      priceAlreadyConverted = true;
    }
  }

  const rawSupplierPrice =
    fullDeal?.dealFare?.totalFare ??
    row?.sourcePrice ??
    row?.source_price ??
    row?.price ??
    row?.packagePrice ??
    null;
  const backendFinalIlsPrice =
    ourTotalFare?.priceWithCommissionAndCreditCardFee ??
    ourTotalFare?.priceWithCommission ??
    ourPrice?.displayPriceIls ??
    row?.displayPriceIls ??
    null;
  const displayedPrice = totalPrice != null ? formatMonthlyIlsPrice(totalPrice) : "0₪";
  const { outboundTime, inboundTime } = getFlightDepartureTimes(row);
  const audit = {
    hotelName:
      row?.hotel?.hotelName ||
      fullDeal?.hotel?.hotelName ||
      row?.hotelName ||
      fullDeal?.hotelName ||
      null,
    selectedPriceField,
    rawSupplierPrice,
    sourceCurrency: normalizedCurrency,
    exchangeRate,
    backendFinalIlsPrice,
    rowTotalPriceBeforeFormat: totalPrice,
    displayedPrice,
    usedConvertPrice: false,
    priceAlreadyConverted,
    row,
  };
  const ssotParityTrace = {
    hotelName: audit.hotelName,
    checkIn: row?.depDate || row?.searchDate?.depDate || row?.checkIn || null,
    checkOut: row?.retDate || row?.searchDate?.retDate || row?.checkOut || null,
    outboundTime,
    inboundTime,
    selectedPriceField,
    selectedPrice: totalPrice,
    pricingSnapshot: fullDealPricingSnapshot,
    ourTotalFare,
    monthlyDisplayedPrice: displayedPrice,
    note: "monthly price now follows regular card SSOT priority",
  };

  if (options?.trace === true && import.meta.env.DEV) {
    console.log("[MONTHLY_PRICE_SSOT_PARITY_TRACE]", ssotParityTrace);
    console.log("[MONTHLY_PRICE_CURRENCY_AUDIT]", audit);
  }

  return {
    priceInfo,
    totalPrice: totalPrice != null ? totalPrice : 0,
    selectedPriceField,
    priceAlreadyConverted,
    usedConvertPrice: false,
    displayedPrice,
    audit,
    ssotParityTrace,
  };
};
